import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, TextField, Stack } from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadOrganizations, createOrganizationRecord, updateOrganizationRecord } from './organizationsSlice';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = { ACTIVE: tokens.severity.low, TRIAL: tokens.severity.info, SUSPENDED: tokens.severity.medium, CHURNED: tokens.severity.critical };
const canonical = (value) => String(value || '').trim().toUpperCase();

export default function OrganizationsPage() {
  const dispatch = useDispatch();
  const { items, status, mutationStatus, error } = useSelector((s) => s.organizations);
  const [name, setName] = useState('');

  useEffect(() => { dispatch(loadOrganizations()); }, [dispatch]);

  const active = items.filter((o) => canonical(o.status) === 'ACTIVE').length;
  const clients = items.filter((o) => canonical(o.type) === 'CLIENT').length;

  const handleCreate = () => {
    if (!name.trim()) return;
    dispatch(createOrganizationRecord({ name: name.trim(), type: 'CLIENT' })).unwrap().then(() => setName('')).catch(() => {});
  };

  return (
    <Box>
      <PageHeader title="Organization & Client Management" subtitle="Every tenant on the platform — internal and client organizations" />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Organizations" value={items.length} icon={BusinessRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Active" value={active} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Client Orgs" value={clients} /></Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {status === 'loading' && <Alert severity="info" sx={{ mb: 2 }}>Loading organizations…</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField size="small" fullWidth placeholder="New organization name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button variant="contained" disabled={mutationStatus === 'loading' || !name.trim()} onClick={handleCreate}>Add Organization</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Status', 'Industry', 'Country', 'Contact', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((o) => (
              <TableRow key={o.id} hover>
                <TableCell sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{o.name}</TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{o.type}</TableCell>
                <TableCell><Chip label={o.status} size="small" sx={{ color: STATUS_COLOR[canonical(o.status)], border: `1px solid ${STATUS_COLOR[canonical(o.status)]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{o.industry}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{o.country}</TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{o.primaryContactEmail}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" disabled={mutationStatus === 'loading'} onClick={() => {
                      const nextName = window.prompt('Organization name', o.name);
                      if (nextName?.trim() && nextName.trim() !== o.name) dispatch(updateOrganizationRecord({ id: o.id, changes: { name: nextName.trim() } }));
                    }}>Edit</Button>
                    <Button size="small" disabled={mutationStatus === 'loading'} onClick={() => dispatch(updateOrganizationRecord({ id: o.id, changes: { status: canonical(o.status) === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' } }))}>
                      {canonical(o.status) === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
